// ============================================================
// Datei: frontend/src/App.tsx
// Trading Engine v2.2 — Auth + Landing + Toast + Protected
// ============================================================
import React, { useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Link, useLocation, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ToastProvider } from './components/Toast';
import './App.css';

import Dashboard from './pages/Dashboard';
import PnlReport from './pages/PnlReport';
import Accounts from './pages/Accounts';
import Trades from './pages/Trades';
import Journal from './pages/Journal';
import MlDashboard from './pages/MlDashboard';
import Benchmark from './pages/Benchmark';
import MonteCarlo from './pages/MonteCarlo';
import TpoPage from './pages/Tpo';
import Terminal from './pages/terminal/Terminal';
import Settings from './pages/Settings';
import Login from './pages/Login';
import LandingPage from './pages/LandingPage';

const navItems = [
  { path: '/', label: 'Dashboard', icon: '📊' },
  { path: '/pnl', label: 'P&L Report', icon: '💰' },
  { path: '/accounts', label: 'Accounts', icon: '👤' },
  { path: '/trades', label: 'Trades', icon: '📈' },
  { path: '/journal', label: 'Journal', icon: '📓' },
  { path: '/ml', label: 'ML Analysis', icon: '🤖' },
  { path: '/montecarlo', label: 'Monte Carlo', icon: '🎲' },
  { path: '/tpo-charts', label: 'TPO Terminal', icon: '\ud83d\udcc8' },
  { path: '/tpo', label: 'TPO Profile', icon: '📊' },
  { path: '/benchmark', label: 'Benchmark', icon: '📈' },
  { path: '/settings', label: 'Settings', icon: '⚙️' },
];

// ============================================================
// Protected Route
// ============================================================
function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, loading } = useAuth();
  if (loading) return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', background: '#0a0e1a', color: '#64748b', fontSize: 18, flexDirection: 'column', gap: 12 }}>
      <div style={{ fontSize: 40 }}>📊</div>
      <div>Loading Trading Engine...</div>
    </div>
  );
  if (!isAuthenticated) return <Navigate to="/welcome" replace />;
  return <>{children}</>;
}

// ============================================================
// Sidebar
// ============================================================
function Sidebar({ isOpen, onClose }: { isOpen: boolean; onClose: () => void }) {
  const location = useLocation();
  const { user, logout } = useAuth();

  return (
    <>
      <div className={`sidebar-overlay ${isOpen ? 'open' : ''}`} onClick={onClose} />
      <nav className={`sidebar ${isOpen ? 'open' : ''}`}>
        <div className="logo">
          <h2>🟢 Trading Engine</h2>
        </div>
        <ul className="nav-menu">
          {navItems.map(item => (
            <li key={item.path} className={location.pathname === item.path ? 'active' : ''}>
              <Link to={item.path} onClick={onClose}>
                <span className="nav-icon">{item.icon}</span>
                {item.label}
              </Link>
            </li>
          ))}
        </ul>

        {user && (
          <div style={{ marginTop: 'auto', borderTop: '1px solid #1e293b', padding: '16px 18px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
              <div style={{
                width: 36, height: 36, borderRadius: '50%',
                background: 'linear-gradient(135deg, #8b5cf6, #6d28d9)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 16, fontWeight: 700, color: '#fff', flexShrink: 0,
              }}>
                {user.username.charAt(0).toUpperCase()}
              </div>
              <div style={{ overflow: 'hidden' }}>
                <div style={{ color: '#e2e8f0', fontSize: 13, fontWeight: 600, whiteSpace: 'nowrap', textOverflow: 'ellipsis', overflow: 'hidden' }}>
                  {user.username}
                </div>
                <div style={{ color: '#64748b', fontSize: 11, whiteSpace: 'nowrap', textOverflow: 'ellipsis', overflow: 'hidden' }}>
                  {user.email}
                </div>
              </div>
            </div>
            <button
              onClick={() => { logout(); onClose(); }}
              style={{
                width: '100%', padding: '8px 12px', background: '#1e293b',
                border: '1px solid #334155', borderRadius: 8, color: '#ef4444',
                fontSize: 13, fontWeight: 600, cursor: 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
              }}
              onMouseEnter={e => e.currentTarget.style.background = '#334155'}
              onMouseLeave={e => e.currentTarget.style.background = '#1e293b'}
            >
              🚪 Logout
            </button>
          </div>
        )}
      </nav>
    </>
  );
}

// ============================================================
// App Layout (eingeloggt)
// ============================================================
function AppLayout() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const location = useLocation();
  // Terminal füllt den ganzen Bereich ohne Padding (Trading-Terminal).
  const fullBleed = location.pathname === '/tpo-charts';
  const activeTitle = navItems.find(i => i.path === location.pathname)?.label ?? 'Trading Engine';
  return (
    <div className="App">
      {/* Mobile-Header: Hamburger + aktiver Seitentitel (nur < 1024px sichtbar). */}
      <header className="mobile-header">
        <button className="mobile-menu-btn" aria-label="Menü" onClick={() => setSidebarOpen(o => !o)}>
          {sidebarOpen ? '✕' : '☰'}
        </button>
        <span className="mobile-header-title">{activeTitle}</span>
      </header>
      <Sidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />
      <main className={`main-content${fullBleed ? ' full-bleed' : ''}`}>
        <Routes>
          <Route path="/" element={<Dashboard />} />
          <Route path="/pnl" element={<PnlReport />} />
          <Route path="/accounts" element={<Accounts />} />
          <Route path="/trades" element={<Trades />} />
          <Route path="/journal" element={<Journal />} />
          <Route path="/ml" element={<MlDashboard />} />
          <Route path="/montecarlo" element={<MonteCarlo />} />
          <Route path="/tpo" element={<TpoPage />} />
          <Route path="/tpo-charts" element={<Terminal />} />
          <Route path="/benchmark" element={<Benchmark />} />
          <Route path="/settings" element={<Settings />} />
        </Routes>
      </main>
    </div>
  );
}

// ============================================================
// Router
// ============================================================
function AppRouter() {
  const { isAuthenticated, loading } = useAuth();

  if (loading) return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', background: '#0a0e1a', color: '#64748b', fontSize: 18, flexDirection: 'column', gap: 12 }}>
      <div style={{ fontSize: 40 }}>📊</div>
      <div>Loading Trading Engine...</div>
    </div>
  );

  return (
    <Routes>
      {/* Landing Page — öffentlich */}
      <Route path="/welcome" element={
        isAuthenticated ? <Navigate to="/" replace /> : <LandingPage />
      } />
      {/* Login/Register — öffentlich */}
      <Route path="/login" element={
        isAuthenticated ? <Navigate to="/" replace /> : <Login />
      } />
      {/* Alle anderen Routes — geschützt */}
      <Route path="/*" element={
        <ProtectedRoute>
          <AppLayout />
        </ProtectedRoute>
      } />
    </Routes>
  );
}

// ============================================================
// App Entry
// ============================================================
function App() {
  return (
    <AuthProvider>
      <ToastProvider>
        <Router>
          <AppRouter />
        </Router>
      </ToastProvider>
    </AuthProvider>
  );
}

export default App;